import { CalendarBlankIcon, CurrencyGbpIcon, FlagIcon } from '@phosphor-icons/react';

import { ResourceLink } from 'src/features/chat/components/ResourceLink';
import ActionPriorityPill from 'src/features/groups/components/ActionPriorityPill';
import { parseActionPriority } from 'src/features/groups/actionPriority';

import type { PamCostedAction } from './api/types';

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

/** Amount without the currency symbol — the row prefixes a single £ icon. */
const formatAmount = (value: number) => gbp.format(value).replace('£', '');

interface PamCostedActionRowProps {
  action: PamCostedAction;
  /** When set, opens the action modal when the card is clicked. */
  onClick?: () => void;
  disabled?: boolean;
  /** Hide the PAM question link — use on the question page where context is already known. */
  showQuestionLink?: boolean;
}

export default function PamCostedActionRow({
  action,
  onClick,
  disabled = false,
  showQuestionLink = true
}: PamCostedActionRowProps) {
  const content = (
    <div
      className={`flex items-start gap-3 rounded-lg border border-border-subtle bg-background-default px-3 py-3 ${
        onClick && !disabled ? 'cursor-pointer transition-colors hover:bg-bluegray-100 active:bg-bluegray-200' : ''
      }`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={(event) => {
        if (disabled) return;
        // Clicks on the nested question link/buttons act on their own.
        if ((event.target as HTMLElement).closest('a, button, input, label')) return;
        onClick?.();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        // Let Enter/Space on a focused nested link/button do their own thing
        // rather than also opening the action.
        if ((event.target as HTMLElement).closest('a, button, input, label')) return;
        if (onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          {showQuestionLink && (
            <ResourceLink
              content={action.question_ref}
              data={{
                id: action.submission_question_id,
                resource_id: action.submission_question_id,
                resource_type: 'pam_submission_question',
                title: action.question_ref,
                is_wiki: false
              }}
            />
          )}
        </div>
        <p className="mt-1 text-sm font-medium text-content-default">{action.title}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-content-subtle">
          <span className="inline-flex items-center gap-1 lining-nums">
            <CalendarBlankIcon size={14} />
            {action.due_date ?? 'No due date'}
          </span>
          <span className="inline-flex items-center gap-1">
            <FlagIcon size={14} />
            <ActionPriorityPill priority={parseActionPriority(action.priority)} />
          </span>
          <span className="capitalize">{action.status}</span>
          {/* Break the cost out into its revenue/capital split when either
              component is set (all PAM costed actions carry the split — legacy
              ones were migrated to revenue), so the plan differentiates the two
              rather than showing only the aggregated total. Falls back to the
              single total for any action without a split. */}
          {action.revenue_cost !== null || action.capital_cost !== null ? (
            <>
              {action.revenue_cost !== null && (
                <span className="inline-flex items-center gap-1 lining-nums tabular-nums text-content-default">
                  <CurrencyGbpIcon size={14} className="text-content-subtle" />
                  Revenue {formatAmount(action.revenue_cost)}
                </span>
              )}
              {action.capital_cost !== null && (
                <span className="inline-flex items-center gap-1 lining-nums tabular-nums text-content-default">
                  <CurrencyGbpIcon size={14} className="text-content-subtle" />
                  Capital {formatAmount(action.capital_cost)}
                </span>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 lining-nums tabular-nums text-content-default">
              <CurrencyGbpIcon size={14} className="text-content-subtle" />
              {action.cost === null ? '—' : formatAmount(action.cost)}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return content;
}
